import {FC} from 'react';
import {Avatar, Box, Button, Grid, Typography} from '@mui/material';
import {notificationTypeTranslation, notificationTypeTranslationMessage,} from '../../../utils/consts';
import {FormattedMessage, useIntl} from 'react-intl';
import {
    avatarBoxSx,
    avatarGapSx,
    avatarSx,
    cardTextSx,
    containerSx,
    dateBoxLgSx,
    dateBoxSmSx,
    dateSx,
    gridContainerSx,
    headerSx,
    infoBlockSx,
    readBoxSx,
    titleTextSx,
    unreadLineSx,
    unreadSx,
    visibilitySx,
} from './NotificationInfoSection.sx';
import dayjs from 'dayjs';
import {Link, useNavigate} from 'react-router-dom';
import {AppRouteEnum, AppRouteSpace} from '../../../utils/helper';
import {NotificationTypeEnum} from '../../../types';
import {NotificationInfoSectionProps} from './NotificationInfoSection.types';
import {Visibility} from '@mui/icons-material';
import {handleMarkAsUnread} from '../NotificationCard';
import {notificationApi} from '../../../api';
import {useBusinessId, useAppSelector} from '../../../app/hooks';
import {roleTextSx} from '../NotificationCard/NotificationCard.sx';
import {UserTypeEnum} from '../../../enums';

export const NotificationInfoSection: FC<NotificationInfoSectionProps> = ({data, location}) => {
    // The business being worked in: the caller's own, or the one a platform administrator picked.
    // Reading `authInfo.business.id` directly was a crash for them - they have no business of
    // their own, and null has no id.
    const businessId = useBusinessId();
    const intl = useIntl();
    const user = data?.user;
    const [setAsUnread] = notificationApi.useMarkNotificationAsUnreadMutation();
    const {authInfo} = useAppSelector((state) => state.common);
    const navigate = useNavigate();

    const isCompanyUser = user
        ? user.userType === UserTypeEnum.COMPANY
        : false;

    let userName = '';

    if (data?.type === NotificationTypeEnum.NEW_COMPANY_REGISTRATION_REQUEST) {
        try {
            const messageData = JSON.parse(data.message || '{}');
            userName = messageData.companyName ?? 'Company';
        } catch {
            userName = 'Company';
        }
    } else if (user) {
        if (isCompanyUser) {
            userName = user?.company?.name ?? 'Company User';
        } else {
            // For drivers, use firstName lastName or fallback to emailAddress
            if (user.firstName && user.lastName) {
                userName = `${user.firstName} ${user.lastName}`;
            } else {
                userName = user.emailAddress ?? 'User';
            }
        }
    } else if (data?.location) {
        userName = data?.location?.name;
    } else {
        userName = 'User';
    }

    const contract = user?.contracts?.find((contract) => contract.active);

    if (!data) {
        return null;
    }

    const handleUnreadClick = () => {
        handleMarkAsUnread(setAsUnread, Number(data.id), businessId, data.type);
        navigate(AppRouteSpace([AppRouteEnum.app, AppRouteEnum.notifications]));
    };

    return (
        <Box sx={containerSx}>
            <Box sx={headerSx}>
                <Box sx={avatarBoxSx}>
                    <Box sx={avatarGapSx}>
                        <Avatar
                            src={user?.avatar?.url}
                            sx={avatarSx}
                        />
                    </Box>
                    <Box>
                        <Typography variant="h4">{userName}</Typography>
                        <Box sx={dateBoxSmSx}>
                            <Typography
                                sx={dateSx}
                                component="span"
                            >
                                {dayjs(data.created).format('DD.MM.YYYY hh:mm')}
                            </Typography>
                        </Box>
                        <Typography
                            sx={roleTextSx}
                            component="span"
                        >
                            {notificationTypeTranslation(intl)[data.type]}
                        </Typography>
                    </Box>
                </Box>
                <Box sx={dateBoxLgSx}>
                    <Typography
                        sx={dateSx}
                        component="span"
                    >
                        {dayjs(data.created).format('DD.MM.YYYY hh:mm')}
                    </Typography>
                </Box>
            </Box>
            <Grid
                container
                spacing={2}
                sx={gridContainerSx}
            >
                {user && (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Typography variant="h4">
                            <FormattedMessage id="contractInfo"/>
                        </Typography>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id={isCompanyUser ? "companyName" : "evDriverName"}/>
                            </Typography>
                            <Link to={AppRouteSpace([AppRouteEnum.app, AppRouteEnum.users, String(user?.id)])}>
                                <Typography
                                    sx={cardTextSx}
                                    color="primary"
                                >
                                    {isCompanyUser
                                        ? user?.company?.name ?? 'Company User'
                                        : (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.emailAddress ?? 'User')}
                                </Typography>
                            </Link>
                        </Box>
                        {user?.phoneNumber && (
                            <Box sx={infoBlockSx}>
                                <Typography sx={titleTextSx}>
                                    <FormattedMessage id="phone"/>
                                </Typography>
                                <Typography sx={cardTextSx}>{user?.phoneNumber}</Typography>
                            </Box>
                        )}
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="email"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{user?.emailAddress}</Typography>
                        </Box>
                        {contract && (
                            <Box sx={infoBlockSx}>
                                <Typography sx={titleTextSx}>
                                    <FormattedMessage id="contractId"/>
                                </Typography>
                                <Link
                                    to={AppRouteSpace([
                                        AppRouteEnum.app,
                                        AppRouteEnum.contracts,
                                        String(contract?.id),
                                    ])}
                                >
                                    <Typography
                                        sx={cardTextSx}
                                        color="primary"
                                    >
                                        {contract?.name}
                                    </Typography>
                                </Link>
                            </Box>
                        )}
                    </Grid>
                )}
                {data.type === NotificationTypeEnum.NEW_LOCATION && (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Typography variant="h4">
                            <FormattedMessage id="location"/>
                        </Typography>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="id"/>
                            </Typography>
                            <Link
                                to={AppRouteSpace([
                                    AppRouteEnum.app,
                                    AppRouteEnum.locations,
                                    String(data?.location?.id),
                                ])}
                            >
                                <Typography
                                    sx={cardTextSx}
                                    color="primary"
                                >
                                    {data?.location?.id}
                                </Typography>
                            </Link>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="address"/>
                            </Typography>
                            <Typography sx={cardTextSx}>
                                {`${data?.location?.address?.country}, ${data?.location?.address?.city}, ${data?.location?.address?.street}, ${data?.location?.address?.houseNumber}`}
                            </Typography>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="zipCode"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{data?.location?.address?.zipCode}</Typography>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="parkingLots"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{data?.location?.parkingLots}</Typography>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="availableAmperes"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{data?.location?.availableAmperes}</Typography>
                        </Box>
                    </Grid>
                )}
                {(data.type === NotificationTypeEnum.NEW_CONTRACT_EXIST_LOCATION ||
                    data.type === NotificationTypeEnum.NEW_CONTRACT_NEW_LOCATION ||
                    data.type === NotificationTypeEnum.STATION_ASSOCIATION ||
                    data.type === NotificationTypeEnum.NEW_USER_REGISTRATION ||
                    data.type === NotificationTypeEnum.NEW_RFID_REQUEST) && (
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Typography variant="h4">
                            <FormattedMessage id="parkingAddress"/>
                        </Typography>
                        {data.type === NotificationTypeEnum.NEW_CONTRACT_EXIST_LOCATION && (
                            <Box sx={infoBlockSx}>
                                <Typography sx={titleTextSx}>
                                    <FormattedMessage id="location"/>
                                </Typography>
                                <Link
                                    to={AppRouteSpace([
                                        AppRouteEnum.app,
                                        AppRouteEnum.locations,
                                        String(user?.location?.id),
                                    ])}
                                >
                                    <Typography
                                        sx={cardTextSx}
                                        color="primary"
                                    >
                                        {user?.location?.name}
                                    </Typography>
                                </Link>
                            </Box>
                        )}
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="address"/>
                            </Typography>
                            <Typography sx={cardTextSx}>
                                {`${user?.parkingAddress?.country}, ${user?.parkingAddress?.city}, ${user?.parkingAddress?.street}, ${user?.parkingAddress?.houseNumber}`}
                            </Typography>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="zipCode"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{user?.parkingAddress?.zipCode}</Typography>
                        </Box>
                        <Box sx={infoBlockSx}>
                            <Typography sx={titleTextSx}>
                                <FormattedMessage id="parkingLot"/>
                            </Typography>
                            <Typography sx={cardTextSx}>{user?.parkingAddress?.parkingPlace}</Typography>
                        </Box>
                        {data.type === NotificationTypeEnum.NEW_CONTRACT_EXIST_LOCATION && (
                            <Box sx={infoBlockSx}>
                                <Typography sx={titleTextSx}>
                                    <FormattedMessage id="availableAmperes"/>
                                </Typography>
                                <Typography sx={cardTextSx}>{location?.availableAmperes}</Typography>
                            </Box>
                        )}
                    </Grid>
                )}
            </Grid>
            {data?.message && data.type === NotificationTypeEnum.NEW_COMPANY_REGISTRATION_REQUEST && (() => {
                try {
                    const messageData = JSON.parse(data.message || '{}');
                    return (
                        <Grid
                            container
                            spacing={2}
                            sx={gridContainerSx}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <Box sx={infoBlockSx}>
                                    <Typography
                                        variant="h4"
                                        mb={3}
                                    >
                                        Company Details
                                    </Typography>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="companyName"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.companyName}</Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            Contact Person
                                        </Typography>
                                        <Typography
                                            sx={cardTextSx}>{messageData.firstName} {messageData.lastName}</Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="emailAddress"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.email}</Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="phoneNumber"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.phoneNumber}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <Box sx={infoBlockSx}>
                                    <Typography
                                        variant="h4"
                                        mb={3}
                                    >
                                        Request Details
                                    </Typography>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            Stations Quantity
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.stationsQuantity}</Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="address"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>
                                            {messageData.parkingAddress?.street} {messageData.parkingAddress?.houseNumber}, {messageData.parkingAddress?.city}, {messageData.parkingAddress?.country}
                                        </Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="zipCode"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.parkingAddress?.zipCode}</Typography>
                                    </Box>
                                    <Box sx={infoBlockSx}>
                                        <Typography sx={titleTextSx}>
                                            <FormattedMessage id="region"/>
                                        </Typography>
                                        <Typography sx={cardTextSx}>{messageData.parkingAddress?.region}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>
                    );
                } catch {
                    return (
                        <Grid
                            container
                            spacing={2}
                            sx={gridContainerSx}
                        >
                            <Grid
                                item
                                xs={12}
                                sm={6}
                            >
                                <Box sx={infoBlockSx}>
                                    <Typography
                                        variant="h4"
                                        mb={3}
                                    >
                                        <FormattedMessage id="message"/>
                                    </Typography>
                                    {data.message}
                                </Box>
                            </Grid>
                        </Grid>
                    );
                }
            })()}
            {data?.message && data.type !== NotificationTypeEnum.NEW_COMPANY_REGISTRATION_REQUEST && (
                <Grid
                    container
                    spacing={2}
                    sx={gridContainerSx}
                >
                    <Grid
                        item
                        xs={12}
                        sm={6}
                    >
                        <Box sx={infoBlockSx}>
                            <Typography
                                variant="h4"
                                mb={3}
                            >
                                <FormattedMessage id="message"/>
                            </Typography>
                            {notificationTypeTranslationMessage(intl, data)[data.type]}
                        </Box>
                    </Grid>
                </Grid>
            )}
            <Grid
                container
                spacing={2}
                sx={gridContainerSx}
            >
                <Grid
                    item
                    xs={12}
                >
                    <Box sx={unreadLineSx}>
                        <Box sx={readBoxSx}>
                            <Visibility sx={visibilitySx}/>
                            <Typography sx={dateSx}>
                                <FormattedMessage
                                    id="readByOn"
                                    values={{
                                        name: `${data?.readByManager?.firstName} ${data?.readByManager?.lastName}`,
                                        date: dayjs(data?.readAt).format('DD.MM.YYYY hh:mm'),
                                    }}
                                />
                            </Typography>
                        </Box>
                        <Button
                            sx={unreadSx}
                            onClick={handleUnreadClick}
                        >
                            <FormattedMessage id="markAsUnread"/>
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};
